import { Component } from '@theme/component';

/**
 * A lightweight horizontal carousel for video sections.
 *
 * The section markup renders the standard slideshow-arrow buttons, and this component
 * handles the scrolling behavior plus the disabled state for the arrows.
 */
export class VideoCarouselComponent extends Component {
  requiredRefs = ['scroller', 'previous', 'next'];

  connectedCallback() {
    super.connectedCallback();

    this.#resizeObserver = new ResizeObserver(() => this.updateControls());
    this.#resizeObserver.observe(this.refs.scroller);

    this.refs.scroller.addEventListener('scroll', this.#handleScroll, { passive: true });
    this.updateControls();
  }

  updatedCallback() {
    super.updatedCallback();
    this.updateControls();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver?.disconnect();
    this.refs.scroller?.removeEventListener('scroll', this.#handleScroll);
  }

  previous() {
    this.#scroll(-1);
  }

  next() {
    this.#scroll(1);
  }

  updateControls() {
    const { scroller, previous, next } = this.refs;
    const hasOverflow = scroller.scrollWidth > scroller.clientWidth + 1;

    if (!hasOverflow) {
      previous.disabled = true;
      next.disabled = true;
      return;
    }

    const atStart = scroller.scrollLeft <= 1;
    const atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1;

    previous.disabled = atStart;
    next.disabled = atEnd;
  }

  #scroll(direction) {
    const { scroller } = this.refs;
    const distance = this.#scrollStepDistance(scroller);
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    scroller.scrollBy({
      left: direction * distance,
      behavior,
    });
  }

  /** 单条宽度小于轨道时，按「卡片宽 + gap」翻页，避免仍用整屏 clientWidth 跳过头 */
  #scrollStepDistance(scroller) {
    const slide = scroller.querySelector(':scope > *');
    if (!slide) return scroller.clientWidth;

    const slideWidth = slide.getBoundingClientRect().width;
    const styles = getComputedStyle(scroller);
    const rawGap = styles.columnGap || styles.gap || '0';
    const gap = Number.parseFloat(rawGap) || 0;

    const step = slideWidth + gap;
    if (step <= 1) return scroller.clientWidth;
    return step;
  }

  #handleScroll = () => {
    this.updateControls();
  };

  #resizeObserver = null;
}

if (!customElements.get('video-carousel-component')) {
  customElements.define('video-carousel-component', VideoCarouselComponent);
}
